// src/components/Stream/StreamPlayer.jsx

import React, { useState, useEffect, useContext } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Box, IconButton, Slider, Stack } from "@mui/material";

import SkipPreviousRoundedIcon from "@mui/icons-material/SkipPreviousRounded";
import SkipNextRoundedIcon from "@mui/icons-material/SkipNextRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseOutlinedIcon from "@mui/icons-material/PauseOutlined";
import VolumeUpOutlinedIcon from "@mui/icons-material/VolumeUpOutlined";
import VolumeDownOutlinedIcon from "@mui/icons-material/VolumeDownOutlined";
import VolumeOffOutlinedIcon from "@mui/icons-material/VolumeOffOutlined";

// Action that updates track playback state in Redux
import { start } from "../../action/streamSong";
import { SocketContext } from "../../context/socket";
import { getUserId } from "../../constants/function";
import { useLocation } from "react-router-dom";

export default function StreamPlayer({ roomId: propRoomId }) {

  const dispatch = useDispatch();
  const { index, songs, track, vol } = useSelector((state) => state.streamSong);
  const { streamCount, streamData } = useSelector((state) => state.streamCount);
  const { Socket } = useContext(SocketContext) || {};
  const socket = Socket;
  const location = useLocation();
  
  // Use prop roomId first, then fallback to streamData or URL parsing
  const roomIdFromUrl = location.pathname.split('/').pop();
  const roomId = propRoomId || streamData?._id || roomIdFromUrl;
  
  // Debug URL parsing
  console.log('🌍 [StreamPlayer Debug] URL Info:', {
    pathname: location.pathname,
    pathSegments: location.pathname.split('/'),
    propRoomId,
    roomIdFromUrl,
    streamDataId: streamData?._id,
    finalRoomId: roomId
  });

  // Local UI state: volume slider, progress bar
  const [showVolume, setShowVolume] = useState(false);
  const [volume, setVolume] = useState(vol ?? 80);
  const [progress, setProgress] = useState(0);

  // Check if there's a valid track
  const hasTrack = !!track && index >= 0 && songs[index];
  const hasValidTrackData = hasTrack;
  
  // Debug what's available in Redux state
  console.log('🎵 [StreamPlayer Debug] Redux state:', {
    streamSong: { index, songs: songs?.length, track: !!track, vol },
    streamCount: { streamCount, streamData },
    hasSocket: !!socket,
    hasValidTrackData,
    trackDetails: track ? { currentState: track.currentState, duration: track.duration } : null,
    roomId,
    roomIdFromUrl
  });
  const hasAnyMusicData = songs.length > 0 && index >= 0; // Show controls if we have any music data
  const shouldShowControls = hasValidTrackData || hasAnyMusicData; // Show controls more persistently
  const duration = hasTrack ? Math.floor(track.duration) || 0 : 0;
  const isPlaying = hasTrack && track.currentState === "playing";

  /************************************************************************
   * (1) onEnded => auto-next
   ************************************************************************/
  useEffect(() => {
    if (!hasTrack) {
      return;
    }

    track.onEnded = () => {
      if (index < songs.length - 1) {
        handleNextSong();
      } else {
        console.log("[StreamPlayer] => final track => no next song");
      }
    };

    // Cleanup
    return () => {
      if (track) {
        track.onEnded = null;
      }
    };
  }, [hasTrack, track, index, songs]);

  /************************************************************************
   * (2) Poll track.getCurrentTime => local UI progress & dispatch pos
   ************************************************************************/
  useEffect(() => {

    const intervalId = setInterval(() => {
      if (hasValidTrackData && track.currentState === "playing") {
        const currentTime = track.getCurrentTime(); // in seconds
        setProgress(currentTime);

        // (IMPORTANT) Dispatch updated pos to Redux
        // so the aggregator in Streaming.jsx can see new pos each second.
        dispatch(
          start({
            index,
            songs,
            pos: currentTime,
            track,
            vol: volume,
          })
        );

        // If near the end, forcibly treat it as ended
        if (duration > 0 && currentTime + 1 >= duration) {
          setProgress(duration);
          handleNextSong();
        }
      }
    }, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [hasValidTrackData, track, duration, dispatch, index, songs, volume]);

  /************************************************************************
   * (2.5) Emit song-start events when track changes
   ************************************************************************/
  useEffect(() => {
    // Debug logging to see what's happening
    console.log('🎵 [StreamPlayer Debug] Checking emission conditions:', {
      hasValidTrackData,
      hasSocket: !!socket,
      streamDataId: streamData?._id,
      trackState: track?.currentState,
      songsLength: songs?.length,
      index,
      duration
    });

    if (hasValidTrackData && socket && roomId) {
      const currentSong = songs[index];
      const songData = {
        _id: currentSong._id,
        title: currentSong.title,
        artist: currentSong.artist || currentSong.uploader?.displayArtistName || 'Unknown',
        duration: duration * 1000, // Convert to milliseconds
        thumbnailImg: currentSong.thumbnailImg, // Add thumbnail for mobile display
        roomId: roomId,
        ownerId: getUserId(),
        type: track.currentState === "playing" ? "start" : "pause",
        pos: progress,
        timestamp: Date.now()
      };

      console.log('🎵 Emitting song-start event:', songData);
      socket.emit("song-start", songData);
    } else {
      console.log('🎵 [StreamPlayer Debug] NOT emitting - missing conditions:', {
        hasValidTrackData,
        hasSocket: !!socket,
        roomId,
        roomIdFromUrl,
        streamDataId: streamData?._id
      });
    }
  }, [hasValidTrackData, track?.currentState, index, socket, roomId, songs, duration, progress]);

  /************************************************************************
   * (2.6) Emit song progress updates periodically
   ************************************************************************/
  useEffect(() => {
    if (!hasValidTrackData || !socket || !roomId || track.currentState !== "playing") {
      return;
    }

    const progressInterval = setInterval(() => {
      const currentTime = track.getCurrentTime();
      const currentSong = songs[index];
      
      const progressData = {
        _id: currentSong._id,
        thumbnailImg: currentSong.thumbnailImg, // Add thumbnail for mobile display
        roomId: roomId,
        ownerId: getUserId(),
        type: "progress",
        pos: currentTime,
        duration: duration * 1000,
        timestamp: Date.now()
      };

      socket.emit("song-start", progressData);
    }, 2000); // Every 2 seconds

    return () => {
      clearInterval(progressInterval);
    };
  }, [hasValidTrackData, track?.currentState, socket, roomId, index, songs, duration]);

  /************************************************************************
   * (3) If Redux volume changes => local volume
   ************************************************************************/
  useEffect(() => {
    setVolume(vol ?? 80);
  }, [vol]);

  /************************************************************************
   * (4) handleNextSong / handlePrevSong
   ************************************************************************/
  function handlePrevSong() {
    if (!hasTrack || index <= 0) return;

    const currentSong = songs[index];
    
    // Emit stop event for current song
    if (socket && roomId && currentSong) {
      const stopData = {
        _id: currentSong._id,
        thumbnailImg: currentSong.thumbnailImg,
        roomId: roomId,
        ownerId: getUserId(),
        type: "stop",
        pos: progress,
        duration: duration * 1000,
        timestamp: Date.now()
      };
      console.log('🎵 Emitting stop event (prev):', stopData);
      socket.emit("song-start", stopData);
    }

    track.stopProcessAudioBuffer();

    dispatch(
      start({
        index: index - 1,
        songs,
        pos: 0,
        track: null,
        vol: volume,
      })
    );
  }

  function handleNextSong() {
    if (!hasTrack || index >= songs.length - 1) return;

    const currentSong = songs[index];
    
    // Emit stop event for current song
    if (socket && roomId && currentSong) {
      const stopData = {
        _id: currentSong._id,
        thumbnailImg: currentSong.thumbnailImg,
        roomId: roomId,
        ownerId: getUserId(),
        type: "stop",
        pos: progress,
        duration: duration * 1000,
        timestamp: Date.now()
      };
      console.log('🎵 Emitting stop event (next):', stopData);
      socket.emit("song-start", stopData);
    }

    track.stopProcessAudioBuffer();

    dispatch(
      start({
        index: index + 1,
        songs,
        pos: 0,
        track: null,
        vol: volume,
      })
    );
  }

  /************************************************************************
   * (5) Play/Pause
   ************************************************************************/
  function handlePlayPause() {
    if (!hasTrack) return;

    const currentSong = songs[index];
    const wasPlaying = track.currentState === "playing";

    if (track.currentState === "playing") {
      track.pauseProcessAudioBuffer();
      
      // Emit pause event
      if (socket && roomId && currentSong) {
        const pauseData = {
          _id: currentSong._id,
          thumbnailImg: currentSong.thumbnailImg,
          roomId: roomId,
          ownerId: getUserId(),
          type: "pause",
          pos: progress,
          duration: duration * 1000,
          timestamp: Date.now()
        };
        console.log('🎵 Emitting pause event:', pauseData);
        socket.emit("song-start", pauseData);
      }
    } else if (track.currentState === "paused") {
      track.resumeProcessAudioBuffer();
      
      // Emit resume event
      if (socket && roomId && currentSong) {
        const resumeData = {
          _id: currentSong._id,
          thumbnailImg: currentSong.thumbnailImg,
          roomId: roomId,
          ownerId: getUserId(),
          type: "start",
          pos: progress,
          duration: duration * 1000,
          timestamp: Date.now()
        };
        console.log('🎵 Emitting resume event:', resumeData);
        socket.emit("song-start", resumeData);
      }
    } else {
      track.startProcessAudioBuffer();
      
      // Emit start event
      if (socket && roomId && currentSong) {
        const startData = {
          _id: currentSong._id,
          thumbnailImg: currentSong.thumbnailImg,
          roomId: roomId,
          ownerId: getUserId(),
          type: "start",
          pos: 0,
          duration: duration * 1000,
          timestamp: Date.now()
        };
        console.log('🎵 Emitting start event:', startData);
        socket.emit("song-start", startData);
      }
    }
  }

  /************************************************************************
   * (6) Seek
   ************************************************************************/
  function handleSeek(_, newVal) {
    if (!hasTrack) return;

    if (typeof track.seekAudioBuffer === "function") {
      track.seekAudioBuffer(newVal);
    } else {
      console.warn("[StreamPlayer] => no track.seekAudioBuffer => can't seek");
    }
    setProgress(newVal);

    // Also dispatch new pos, so aggregator sees the skip
    dispatch(
      start({
        index,
        songs,
        pos: newVal,
        track,
        vol: volume,
      })
    );
  }

  /************************************************************************
   * (7) Volume
   ************************************************************************/
  function handleVolumeSlider(_, newVal) {
    setVolume(newVal);

    if (hasTrack && typeof track.setVolume === "function") {
      track.setVolume(newVal);
    }
  }

  function handleVolumeButton(e) {
    e.stopPropagation();
    if (volume === 0) {
      setShowVolume(true);
      handleVolumeSlider(null, 50);
    } else {
      setShowVolume(!showVolume);
    }
  }

  /************************************************************************
   * (8) Render
   ************************************************************************/
  return (
    <Box
      sx={{
        position: "absolute",
        bottom: 0,
        width: "100%",
        background: "#1e1e1e",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        height: 55,
      }}
      onClick={() => setShowVolume(false)}
    >
      {shouldShowControls && (
        <>
          {/* Slider for Track Progress */}
          <Slider
            size="small"
            value={hasValidTrackData ? progress : 0}
            max={duration}
            onChange={hasValidTrackData ? handleSeek : () => {}}
            disabled={!hasValidTrackData}
            aria-label="Song Progress"
            valueLabelDisplay="auto"
            sx={{
              padding: 0,
              height: 5,
              position: "absolute",
              top: 0,
              "& .MuiSlider-thumb": {
                width: 8,
                height: 8,
                transition: "0.3s",
                "&.Mui-active": { width: 14, height: 14 },
              },
              "& .MuiSlider-rail": { opacity: 0.28 },
            }}
          />

          {/* Playback Controls */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-evenly"
            sx={{ height: 50, background: "#1e1e1e" }}
          >
            {/* Previous Button */}
            <IconButton
              aria-label="Previous track"
              onClick={hasValidTrackData ? handlePrevSong : () => {}}
              disabled={!hasValidTrackData || index === 0}
            >
              <SkipPreviousRoundedIcon
                sx={{
                  fontSize: 22,
                  color: (!hasValidTrackData || index === 0) ? "gray" : "#fff",
                }}
              />
            </IconButton>

            {/* Play/Pause Button */}
            <IconButton
              aria-label="Play/Pause"
              sx={{ 
                backgroundColor: "#2E2E2D",
                opacity: hasValidTrackData ? 1 : 0.5
              }}
              onClick={hasValidTrackData ? handlePlayPause : () => {}}
              disabled={!hasValidTrackData}
            >
              {hasValidTrackData && isPlaying ? (
                <PauseOutlinedIcon sx={{ fontSize: 22 }} />
              ) : (
                <PlayArrowRoundedIcon sx={{ fontSize: 22 }} />
              )}
            </IconButton>

            {/* Next Button */}
            <IconButton
              aria-label="Next track"
              onClick={hasValidTrackData ? handleNextSong : () => {}}
              disabled={!hasValidTrackData || index >= songs.length - 1}
            >
              <SkipNextRoundedIcon
                sx={{
                  fontSize: 22,
                  color: (!hasValidTrackData || index >= songs.length - 1) ? "gray" : "#fff",
                }}
              />
            </IconButton>

            {/* Volume Section */}
            <Box
              sx={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                height: 100,
              }}
            >
              {showVolume && (
                <Box sx={{ position: "absolute", bottom: 0 }}>
                  <Slider
                    orientation="vertical"
                    value={volume}
                    max={100}
                    step={1}
                    onChange={handleVolumeSlider}
                    sx={{
                      height: 150,
                      width: 6,
                      "& .MuiSlider-thumb": {
                        width: 12,
                        height: 12,
                        transition: "0.3s",
                      },
                      "& .MuiSlider-track": { width: 6 },
                      "& .MuiSlider-rail": { width: 6 },
                    }}
                  />
                </Box>
              )}

              <IconButton onClick={handleVolumeButton}>
                {volume === 0 ? (
                  <VolumeOffOutlinedIcon sx={{ fontSize: 22 }} />
                ) : volume < 50 ? (
                  <VolumeDownOutlinedIcon sx={{ fontSize: 22 }} />
                ) : (
                  <VolumeUpOutlinedIcon sx={{ fontSize: 22 }} />
                )}
              </IconButton>
            </Box>
          </Stack>
        </>
      )}
    </Box>
  );
}
